/**
 * Дашборд подбора для ОИ/HR (ТЗ-2026-011 ФТ-13) — страница /processes/recruitment (кнопка в шапке «Процессов»).
 * Бэк: GET /edo/recruitment/dashboard (HR — только свои компании, admin — все; прочим 403).
 * Вкладка была убрана 07.08.2026 (d165c13) без записанной причины, возвращена 08.10.2026.
 */
import { useState } from 'react'
import { useRouter } from 'expo-router'
import { Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Card, Icon, OptionSelect, PressCard, QueryState } from '../../../ui'
import { useDepartments } from '../../users/useUsers'
import { useRecruitmentDashboard, useRecruitmentGrades } from '../useEdo'
import { docNo, GROUP_LABEL } from '../processVM'
import { EdoStatusBadge } from './EdoStatusBadge'

function Stat({ label, value, tn }: { label: string; value: string | number; tn: string }) {
  return (
    <YStack flex={1} minWidth={130} padding={14} borderRadius={9} borderWidth={1} borderColor="$borderColor" backgroundColor="$background" gap={4}>
      <Text fontSize={22} fontWeight="800" color={tone(tn).text}>{value}</Text>
      <Text fontSize={11} color="$muted">{label}</Text>
    </YStack>
  )
}

export function RecruitmentDashboard() {
  const { t, lang } = useApp()
  const router = useRouter()
  const grades = useRecruitmentGrades()
  const deps = useDepartments()
  const [status, setStatus] = useState('')
  const [grade, setGrade] = useState('')
  const [dept, setDept] = useState('')
  const dash = useRecruitmentDashboard({ status: status || undefined, grade: grade || undefined, department: dept || undefined })

  const statuses = [
    { value: 'MGR_REVIEW', label: t({ ru: 'У руководителя', en: 'Manager' }) },
    { value: 'SUP_REVIEW', label: t({ ru: 'У рук. СП', en: 'Supervisor' }) },
    { value: 'CEO_REVIEW', label: t({ ru: 'У ГД', en: 'CEO' }) },
    { value: 'HR_REVIEW', label: t({ ru: 'У ОИ/HR', en: 'HR' }) },
    { value: 'IN_PROGRESS', label: t({ ru: 'В работе', en: 'In progress' }) },
    { value: 'SEARCHING', label: t({ ru: 'Поиск', en: 'Searching' }) },
    { value: 'REWORK', label: t({ ru: 'Коррекция', en: 'Rework' }) },
    { value: 'CLOSED', label: t({ ru: 'Закрытые', en: 'Closed' }) },
    { value: 'CANCELLED', label: t({ ru: 'Отменённые', en: 'Cancelled' }) },
    { value: 'REJECTED', label: t({ ru: 'Отклонённые', en: 'Rejected' }) },
  ]
  const one = (set: (v: string) => void) => (n: string[]) => set(n[0] ?? '')
  const d = dash.data

  return (
    <QueryState q={dash}>
      <YStack gap={16}>
        <XStack gap={10} flexWrap="wrap">
          <Stat label={t({ ru: 'Активные', en: 'Active' })} value={d?.stats.active ?? '—'} tn="blue" />
          <Stat label={t({ ru: 'Закрытые', en: 'Closed' })} value={d?.stats.closed ?? '—'} tn="green" />
          <Stat label={t({ ru: 'Отклонённые', en: 'Rejected' })} value={d?.stats.rejected ?? '—'} tn="red" />
          <Stat label={t({ ru: 'Отменённые', en: 'Cancelled' })} value={d?.stats.cancelled ?? '—'} tn="default" />
          <Stat label={t({ ru: 'Ср. дней до закрытия', en: 'Avg days to close' })} value={d?.stats.avgDaysToClose ?? '—'} tn="gold" />
        </XStack>

        {/* Фильтры — те же справочники, что в анкете: бэк сравнивает department/грейд точным равенством. */}
        {/* zIndex: выпадающая панель OptionSelect абсолютна — без него список карточек ниже рисуется поверх. */}
        <XStack gap={10} flexWrap="wrap" zIndex={10}>
          <YStack>
            <OptionSelect list options={statuses} value={status ? [status] : []} onChange={one(setStatus)} placeholder={t({ ru: 'Все статусы', en: 'All statuses' })} />
          </YStack>
          <YStack>
            <OptionSelect
              list
              options={(grades.data ?? []).map((g) => ({ value: g.code, label: lang === 'en' ? g.nameEn : t(g.nameRu) }))}
              value={grade ? [grade] : []}
              onChange={one(setGrade)}
              placeholder={t({ ru: 'Все грейды', en: 'All grades' })}
            />
          </YStack>
          <YStack>
            <OptionSelect list searchable options={(deps.data ?? []).map((x) => x.name)} value={dept ? [dept] : []} onChange={one(setDept)} placeholder={t({ ru: 'Все подразделения', en: 'All departments' })} />
          </YStack>
          {(status || grade || dept) ? (
            <PressCard paddingHorizontal={12} paddingVertical={8} onPress={() => { setStatus(''); setGrade(''); setDept('') }}>
              <Text fontSize={12} color="$primary">{t({ ru: 'Сбросить', en: 'Reset' })}</Text>
            </PressCard>
          ) : null}
        </XStack>

        {!d?.items.length ? (
          <Card padding={20}>
            <Text fontSize={13} color="$muted">{dash.isLoading ? t({ ru: 'Загрузка…', en: 'Loading…' }) : t({ ru: 'Заявок нет.', en: 'No requests.' })}</Text>
          </Card>
        ) : (
          <YStack gap={8}>
            {d.items.map((it) => (
              <PressCard key={it.id} padding={14} gap={6} onPress={() => router.push(`/processes/${it.id}`)}>
                <XStack justifyContent="space-between" alignItems="center" gap={10} flexWrap="wrap">
                  <XStack alignItems="center" gap={8} flex={1} minWidth={140}>
                    <Text fontSize={14} fontWeight="700" color="$color">{it.jobTitle ?? '—'}</Text>
                    <Text fontSize={11} color="$muted">№ {docNo({ docNumber: it.number, id: it.id })}</Text>
                  </XStack>
                  <EdoStatusBadge status={it.status} />
                </XStack>
                <Text fontSize={12} color="$muted">
                  {(it.jobGradeNameRu ? t(it.jobGradeNameRu) : it.jobGradeCode ?? '—')} · {it.department ?? '—'}
                </Text>
                <XStack gap={14} flexWrap="wrap" alignItems="center">
                  <XStack alignItems="center" gap={5}>
                    <Icon name="clock" size={13} color="$muted" />
                    <Text fontSize={12} color="$muted">{t({ ru: `в статусе ${it.daysInStatus} дн.`, en: `${it.daysInStatus}d in status` })}</Text>
                  </XStack>
                  {/* assignee — ФИО или код группы открытой задачи (hr/gd…) */}
                  {it.assignee ? (
                    <Text fontSize={12} color="$muted">
                      {t({ ru: 'ответств.', en: 'owner' })}: {GROUP_LABEL[it.assignee] ? t(GROUP_LABEL[it.assignee]) : it.assignee}
                    </Text>
                  ) : null}
                  <XStack alignItems="center" gap={4} marginLeft="auto">
                    <Text fontSize={12} fontWeight="600" color="$primary">{t({ ru: 'Открыть', en: 'Open' })}</Text>
                    <Icon name="chevron-right" size={14} color="$primary" />
                  </XStack>
                </XStack>
              </PressCard>
            ))}
          </YStack>
        )}
      </YStack>
    </QueryState>
  )
}
